/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useContext, useEffect } from 'react';
import { StoryblokComponent } from 'storyblok-js-client';
import { PageStateContext } from '../../services';
import { Props } from '../types';
import { filterProps, slottedToComponents } from '../helpers';

interface Component {
  component: string;
  _uid: string;
  slotted?: Array<Component>;
}

const pagesTypeMap = {
  page: ['body'],
  'page-product': ['stage', 'general', 'specification', 'system', 'further_information', 'categories'],
  'page-press-news': ['content', 'contact_list', 'content_aside', 'further_content'],
  'page-press-news-proxy': ['content', 'contact_list', 'content_aside', 'further_content'],
  'page-press-overview': [],
  'page-product-overview': [],
  'did-article-page': ['stage', 'intro', 'content_block', 'author_image'],
};

const imageComponentsNames = ['roche-image'];

// eslint-disable-next-line max-len
const isImageComponent = (component: Component): boolean => imageComponentsNames.includes(component.component);

const getImageComponentsRecursively = (component: Component): Array<Component> => {
  const imagesList = [];
  if (isImageComponent(component)) {
    imagesList.push(component);
  }
  const slottedKeys = Object.keys(component).filter((slottedKey: string) => /slotted/.test(slottedKey));
  if (slottedKeys.length > 0) {
    slottedKeys.forEach((key) => {
      const slottedField = component[key];
      if (slottedField.length > 0) {
        slottedField.forEach((slottedComponent: Component) => {
          const imageComponent = getImageComponentsRecursively(slottedComponent);
          if (imageComponent.length > 0) {
            imagesList.push(...imageComponent);
          }
        });
      }
    });
  }
  return imagesList;
};

const getPageContent = (page): Array<Component> => {
  let pageContent;
  try {
    const pageType = page.content.component;
    const sections = pagesTypeMap[pageType] || pagesTypeMap.page;
    pageContent = sections.reduce(
      (list, contentItem) => [...list, ...page.content[contentItem]], [],
    );
  } catch {
    pageContent = [];
  }
  return pageContent;
};

// eslint-disable-next-line max-len
const getImageComponents = (componentsList: Array<Component>) => componentsList.reduce((list, item) => {
  const imageComponents = getImageComponentsRecursively(item);
  if (imageComponents?.length > 0) {
    list.push(...imageComponents);
  }
  return list;
}, []);

export const RochePictureCredits = (
  { blok, getComponent, slot }: Props<StoryblokComponent<string>>,
): JSX.Element => {
  const { page, pictureCreditsAdditionalData } = useContext(PageStateContext);
  const [staticComponents, setStaticComponents] = useState([]);
  const [dynamicComponents, setDynamicComponents] = useState([]);

  useEffect(() => {
    setStaticComponents(getImageComponents(getPageContent(page)));
  }, []);

  useEffect(() => {
    if (Array.isArray(pictureCreditsAdditionalData)) {
      setDynamicComponents(getImageComponents(pictureCreditsAdditionalData));
    }
  }, [pictureCreditsAdditionalData]);

  const { props, slotted } = filterProps(blok);
  const allProps = {
    ...props,
    ...(slot ? { slot } : {}),
    // eslint-disable-next-line no-underscore-dangle
    ...(blok._uid ? { uid: blok._uid } : {}),
    data: JSON.stringify(
      [...staticComponents, ...dynamicComponents].map(
        (componentProps) => filterProps(componentProps).props,
      ),
    ),
  };

  const children = slottedToComponents(getComponent, slotted);

  return React.createElement('roche-picture-credits', allProps, children);
};
